import { toast } from 'react-toastify';
import API from '../apiClient';
import { handleApiError } from '../Utils/errorHandler';

// Fetch control data.
// - With controlId: GET /control/:id  (single record, optionally scoped by organizationId)
// - Without controlId: GET /control?organization_id=  (list, scoped to org when provided)
// Always pass organizationId when calling the list form to prevent cross-org data leakage.
export const GetControlData = (controlId, organizationId) => {
    let url = '/control';
    if (controlId) {
      url += `/${encodeURIComponent(controlId)}`;
      if (organizationId) {
        url += `?organization_id=${encodeURIComponent(organizationId)}`;
      }
    } else if (organizationId) {
      // List scoped to the caller's organization — prevents returning controls from other orgs
      url += `?organization_id=${encodeURIComponent(organizationId)}`;
    }
    return new Promise((resolve) => {
      API.get(url)
        .then(response => {
          resolve(response); 
        })
        .catch(error =>{
          toast.error(error)
          console.error("Failed to get control data:", error)
        }); 
    });
  };

// for list of framework organization
export const GetFrameworkOrganizationData = () => {
    return new Promise((resolve) => {
      API.get(`/fo/`)
        .then(response => {
          resolve(response); 
        })
        .catch(error =>{
          toast.error(error)
          console.error("Failed to get framework organization data:", error)
        }); 
    });
  };


//add control 
export const addControl = (payload) => {
  return new Promise((resolve) => {
      API.post(`/control`, payload)
      .then(response => {
          resolve(response);
      }) .catch(error =>{
          toast.error(error)
          console.error("Failed to add control:", error)
        });
  })

}  

// delete control
export const deleteControl = (entity) => {
  return new Promise((resolve,reject) => {
      API.post(`/control/${encodeURIComponent(entity)}`)
      .then(response => {
          resolve(response);
      }) .catch(error =>{
          reject(error)
          toast.error(error)
          console.error("Failed to delete control:", error)
        });
  })

}

//update control
export const UpdateControl = (entity,payload) => {
  return new Promise((resolve, reject) => {
    API.put(`/control/${encodeURIComponent(entity)}`,payload)
      .then(response => {
        resolve(response);
      }) .catch(error =>{
          reject(error)
          toast.error(error)
          console.error("Failed to update control:", error)
      });
  });
};
 
// for list of control data by organization id & framework id
export const GetControlDataByOrgIdFrameworkId = (temp_framework_id,temp_org_id,domain) => {
  return new Promise((resolve) => {
    API.get(`/control?framework_id=${encodeURIComponent(temp_framework_id)}&organization_id=${encodeURIComponent(temp_org_id)}&domain=${encodeURIComponent(domain)}`)
      .then(response => {
        resolve(response); 
      })
      .catch(error =>{
        toast.error(error)
        console.error("Failed to get control data by org/framework ID:", error)
      }); 
  });
};

// NEW: Get control count only (lightweight for tab badges)
// Falls back to full data fetch if endpoint not available yet
export const GetControlCountByFrameworkId = (frameworkId, organizationId) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Try the new count-only endpoint first
      const response = await API.get(`/control/count?framework_id=${encodeURIComponent(frameworkId)}&organization_id=${encodeURIComponent(organizationId)}`);
      resolve(response);
    } catch (error) {
      // If endpoint doesn't exist (404), fall back to full data fetch
      if (error?.response?.status === 404) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Count endpoint not available, falling back to full data fetch');
        }
        try {
          const fullDataResponse = await GetControlDataByOrgIdFrameworkId(frameworkId, organizationId, "");
          if (fullDataResponse?.status === 200) {
            const rowsData = fullDataResponse?.data || [];
            const total = rowsData.length;
            const implemented = rowsData.filter(row =>
              row.framework_control_organization?.[0]?.fco_status === "Yes"
            ).length;
            const percentage = total > 0 ? Math.round((implemented / total) * 100) : 0;
            
            // Return in same format as count endpoint
            resolve({
              status: 200,
              data: {
                framework_id: frameworkId,
                total,
                implemented,
                percentage
              }
            });
          }
        } catch (fallbackError) {
          reject(fallbackError);
          console.error("Failed to get control count (fallback):", fallbackError);
        }
      } else {
        reject(error);
        console.error("Failed to get control count:", error);
      }
    }
  });
};

// NEW: Batch get control counts (most efficient - 1 request for all frameworks)
// Falls back to individual requests if endpoint not available yet
// Automatically splits into batches of 20 to comply with API limit
export const GetBatchControlCounts = (frameworkIds, organizationId) => {
  return new Promise(async (resolve, reject) => {
    try {
      const BATCH_SIZE = 20;
      
      // If within limit, make single request
      if (frameworkIds.length <= BATCH_SIZE) {
        const response = await API.post(`/control/batch-count`, {
          framework_ids: frameworkIds,
          organization_id: organizationId
        });
        resolve(response);
        return;
      }
      
      // Split into batches of 20
      const batches = [];
      for (let i = 0; i < frameworkIds.length; i += BATCH_SIZE) {
        batches.push(frameworkIds.slice(i, i + BATCH_SIZE));
      }
      
      // Execute all batches in parallel
      const batchResults = await Promise.all(
        batches.map(batch => 
          API.post(`/control/batch-count`, {
            framework_ids: batch,
            organization_id: organizationId
          })
        )
      );
      
      // Merge results from all batches
      const mergedData = {};
      batchResults.forEach(result => {
        const data = result?.data?.data || result?.data || {};
        Object.assign(mergedData, data);
      });
      
      resolve({
        status: 200,
        data: mergedData
      });
    } catch (error) {
      // If endpoint doesn't exist (404), fall back to individual requests
      if (error?.response?.status === 404) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Batch count endpoint not available, falling back to individual requests');
        }
        try {
          // Fetch counts for all frameworks in parallel
          const countPromises = frameworkIds.map(id => 
            GetControlCountByFrameworkId(id, organizationId)
          );
          const results = await Promise.all(countPromises);
          
          // Transform to batch response format
          const batchData = {};
          results.forEach((result, index) => {
            if (result?.status === 200 && result?.data) {
              batchData[frameworkIds[index]] = {
                total: result.data.total,
                implemented: result.data.implemented,
                percentage: result.data.percentage
              };
            }
          });
          
          resolve({
            status: 200,
            data: batchData
          });
        } catch (fallbackError) {
          reject(fallbackError);
          console.error("Failed to get batch control counts (fallback):", fallbackError);
        }
      } else {
        reject(error);
        console.error("Failed to get batch control counts:", error);
      }
    }
  });
};

//add FCO
export const addFrameworkControlOrganization = (payload) => {
  return API.post(`/fco`, payload)
    .then(response => response)
    .catch(error => {
      const msg = error?.response?.data?.error || "Failed to add FCO";
      toast.error(msg);
      console.error("Failed to add FCO:", error);
      throw error;
    });
};  

//update FCO
export const updateFrameworkControlOrganization = (entity,payload) => {
  return API.put(`/fco/${encodeURIComponent(entity)}`,payload)
    .then(response => response)
    .catch(error =>{
      const msg = error?.response?.data?.error || "Failed to update FCO";
      toast.error(msg);
      console.error("Failed to update FCO:", error);
      throw error;
    });
};

// Fast path for a save that changed only plain fields (PATCH /fco/by-control). It updates
// every row of the control in one statement and leaves files, policies, groups and RACI
// users alone. Errors are not toasted here: the caller falls back to POST /fco on
// 403/404/405 (older backend, or a role limited to its own organisation) and reports
// anything else itself.
export const PatchFcoScalars = (payload) => API.patch('/fco/by-control', payload);

// Everything the Overview Control dialog needs for one control, in one call:
//   { control, framework, progress, coverage, related }
// (GET /control/:id/overview). A 404/405 means the backend predates the endpoint (or the
// control is gone); the error then has overviewUnavailable = true so the dialog can run its
// older loaders. Any other failure is reported once here.
export const GetControlOverview = async (controlId, organizationId) => {
  try {
    const response = await API.get(`/control/${encodeURIComponent(controlId)}/overview`, {
      params: organizationId ? { organization_id: organizationId } : undefined,
    });
    return response?.data;
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404 || status === 405) {
      const unavailable = new Error('Control overview is not available');
      unavailable.overviewUnavailable = true;
      throw unavailable;
    }
    throw handleApiError(error, 'Failed to load the control overview', { control_id: controlId });
  }
};

// Bulk FCO update from the Zavvy chat (PATCH /fco/bulk). payload:
//   { fco_ids: [uuid], set: { user_raci?: [{ user_id, raci_role }], status?, remark_append? } }
// Resolves { updated, failed: [{ fco_id, error }] }. Errors are not toasted here: the card shows them.
export const BulkUpdateFco = (payload) => API.patch('/fco/bulk', payload).then((response) => response?.data);

//reset FCO
export const resetFrameworkControlOrganization = (entity) => {
  return API.put(`/fco/${encodeURIComponent(entity)}`, {
    control_id: null,
    status: null,
    group_id: null,
    remark: "",
    policy_id: null,
    file_id: [],
    organization_id: null,
    created_by: null,
    updated_by: null
  })
    .then(response => response)
    .catch(error =>{
      const msg = error?.response?.data?.error || "Failed to reset FCO";
      toast.error(msg);
      console.error("Failed to reset FCO:", error)
      throw error;
    }); 
};

export const GetFcoById = (entity) => {
  return new Promise((resolve) => {
    API.get(`/fco/${entity}`)
      .then(response => {
        resolve(response); 
      })
      .catch(error =>{
        toast.error(error)
        console.error("Failed to get FCO by ID:", error)
      }); 
  });
};

export const GetFcoByControlAndOrg = (controlId, organizationId) => {
  return new Promise((resolve, reject) => {
    API.get(`/fco/by-control?control_id=${encodeURIComponent(controlId)}&organization_id=${encodeURIComponent(organizationId)}`)
      .then(response => {
        resolve(response); 
      })
      .catch(error => {
        toast.error(error);
        console.error("Failed to get FCO by control and org:", error);
        reject(error);
      }); 
  });
};

export const GetFco = () => {
  return new Promise((resolve) => {
    API.get(`/fco/`)
      .then(response => {
        resolve(response); 
      })
      .catch(error =>{
        toast.error(error)
        console.error("Failed to get FCO:", error)
      }); 
  });
};

//reset all control implementation status by deleting FCOs
export const resetAllControlImplementation = (frameworkId, organizationId, progressCallback = () => {}) => {
  return new Promise(async (resolve, reject) => {
    try {
      // First get all controls with their FCOs
      progressCallback({ message: "Fetching controls data..." });
      const controlsResponse = await GetControlDataByOrgIdFrameworkId(frameworkId, organizationId, "");
      
      if (controlsResponse?.status === 200 && controlsResponse.data) {
        // Extract all FCO IDs that need to be deleted
        const fcosToDelete = controlsResponse.data
          .filter(control => control.evidence && control.evidence.length > 0)
          .flatMap(control => control.evidence) // Use flatMap to get all FCOs
          .filter(evidence => evidence.fco_id)
          .map(evidence => evidence.fco_id);
        
        // Update the progress with total count
        progressCallback({ 
          total: fcosToDelete.length,
          current: 0,
          success: 0,
          failed: 0,
          message: `Found ${fcosToDelete.length} control implementations to reset.`
        });
        
        // No controls to reset
        if (fcosToDelete.length === 0) {
          progressCallback({ message: "No control implementations to reset." });
          resolve({ 
            status: 200, 
            message: "No control implementations to reset" 
          });
          return;
        }
        
        // Process deletions sequentially instead of in parallel
        let successCount = 0;
        let failCount = 0;
        
        for (let i = 0; i < fcosToDelete.length; i++) {
          const fcoId = fcosToDelete[i];
          try {
            progressCallback({ 
              current: i + 1, 
              message: `Deleting control implementation ${i + 1} of ${fcosToDelete.length}...` 
            });
            
            await deleteFCO(fcoId);
            successCount++;
            
            progressCallback({ 
              success: successCount,
              message: `Successfully reset control implementation ${i + 1} of ${fcosToDelete.length}`
            });
            
            // Add a small delay between requests to avoid overwhelming the service
            await new Promise(r => setTimeout(r, 100));
          } catch (err) {
            failCount++;
            progressCallback({ 
              failed: failCount,
              message: `Failed to reset control implementation ${i + 1} of ${fcosToDelete.length}`
            });
            console.log(`Failed to delete FCO ${fcoId}:`, err);
            // Continue with the next FCO even if this one fails
          }
        }
        
        // Final progress update
        progressCallback({ 
          message: `Completed: ${successCount} successful, ${failCount} failed`
        });
        
        resolve({ 
          status: 200, 
          message: `Control implementations reset: ${successCount} successful, ${failCount} failed`
        });
      } else {
        progressCallback({ message: "Failed to fetch controls" });
        reject(new Error("Failed to fetch controls"));
      }
    } catch (error) {
      progressCallback({ message: error?.response?.data?.error || "Error resetting control implementations" });
      reject(error);
      toast.error(error?.response?.data?.error || "Error resetting control implementations");
      console.log(error);
    }
  });
};

//delete FCO
export const deleteFCO = (fcoId) => {
  return new Promise((resolve, reject) => {
    API.post(`/fco/${encodeURIComponent(fcoId)}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
        toast.error(error?.response?.data?.error || "Error deleting FCO");
        console.log(error);
      });
  });
};

// Get FCO status for multiple related controls
export const getFCOByControlIDs = (controlIds, organizationId) => {
  return new Promise((resolve, reject) => {
    const controlIdsParam = controlIds.join(',');
    API.get(`/fco/by-control-ids?control_ids=${encodeURIComponent(controlIdsParam)}&organization_id=${encodeURIComponent(organizationId)}`)
      .then(response => {
        resolve(response);
      })
      .catch(error => {
        reject(error);
        // Don't show toast error for this endpoint as it's used for optional data display
        console.log("Error fetching related control FCO data:", error);
      });
  });
};

// Statement of Applicability rows for one framework (ISO), built by IAM from the control list
// and this organisation's implementation status. GET /control/soa
// scope: 'annex_a' (default) | 'all'; filter: 'all' | 'applicable' | 'excluded' | 'not_implemented'
export const GetStatementOfApplicability = (framework_id, organization_id, { scope = 'annex_a', filter = 'all' } = {}) => {
  const params = new URLSearchParams({ framework_id, organization_id, scope, filter });
  return API.get(`/control/soa?${params.toString()}`);
};
